import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, type Location } from 'react-router-dom';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcher } from './ThemeSwitcher';

// Add new pages here. `show` lets an item hide itself in certain routes
// (e.g. the "Abrechnung" call-to-action is redundant on the login and
// abrechnung pages themselves).
type NavItem = {
  to: string;
  labelKey: string;
  show?: (loc: Location) => boolean;
};

const ITEMS: NavItem[] = [
  {
    to: '/',
    labelKey: 'nav.calendar',
    show: (loc) => loc.pathname !== '/',
  },
  {
    to: '/anmelden',
    labelKey: 'nav.submitEntry',
    show: (loc) => loc.pathname !== '/anmelden' && loc.pathname !== '/abrechnung',
  },
  {
    to: '/gast',
    labelKey: 'nav.externalEntry',
    show: (loc) => loc.pathname !== '/gast' && loc.pathname !== '/gast-abrechnung',
  },
  { to: '/impressum', labelKey: 'nav.impressum' },
];

const PANEL_ID = 'nav-drawer-panel';

export function NavDrawer() {
  const { t } = useTranslation();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Auto-close on route change so navigating somewhere dismisses the drawer.
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  // ESC closes; lock body scroll while open; move focus to the close button
  // on open and back to the hamburger on close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      hamburgerRef.current?.focus();
    };
  }, [open]);

  return (
    <>
      <button
        ref={hamburgerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('nav.openMenu')}
        aria-expanded={open}
        aria-controls={PANEL_ID}
        className="inline-flex items-center justify-center rounded-md p-2 text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-500 dark:text-slate-300 dark:hover:bg-slate-800 dark:focus:ring-slate-400"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className="w-6 h-6"
          aria-hidden="true"
        >
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      {/* Backdrop + panel are portalled to document.body so they sit at the
          root of the stacking order, free of any parent stacking context
          (the sticky header would otherwise trap them).

          They live inside a viewport-sized `fixed inset-0 overflow-x-clip`
          wrapper rather than being `fixed` themselves. The closed panel is
          parked `translate-x-full` ~85vw off the right edge; a `position:
          fixed` panel's containing block is the viewport, so html/body's
          `overflow-x: clip` can't catch it and iOS counts it in the
          document's scroll width → horizontal overflow on every page. As an
          `absolute` child of this `fixed` (= positioned) wrapper, the panel
          is clipped by the wrapper's own `overflow-x: clip`, so the
          off-screen portion contributes zero width while the slide
          animation is preserved. The wrapper is `pointer-events-none` while
          closed so it never intercepts taps over the page. */}
      {createPortal(
        <div
          className={
            'fixed inset-0 z-[60] overflow-x-clip ' +
            (open ? '' : 'pointer-events-none')
          }
        >
          <div
            onClick={() => setOpen(false)}
            aria-hidden="true"
            className={
              'absolute inset-0 z-0 bg-slate-900/40 dark:bg-black/60 transition-opacity duration-200 ' +
              (open
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none')
            }
          />
          <aside
            id={PANEL_ID}
            role="dialog"
            aria-modal="true"
            aria-labelledby="nav-drawer-title"
            className={
              'absolute inset-y-0 right-0 z-10 w-72 max-w-[85vw] bg-white dark:bg-slate-900 shadow-xl ' +
              'flex flex-col transform transition-transform duration-200 ' +
              (open ? 'translate-x-0' : 'translate-x-full')
            }
          >
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-3">
              <h2
                id="nav-drawer-title"
                className="text-sm font-semibold tracking-wide uppercase text-slate-600 dark:text-slate-400"
              >
                {t('nav.drawerTitle')}
              </h2>
              <button
                ref={closeRef}
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('nav.closeMenu')}
                className="rounded-md p-1.5 text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-500 dark:text-slate-300 dark:hover:bg-slate-800 dark:focus:ring-slate-400"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              {ITEMS.filter((item) => item.show?.(loc) ?? true).map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="block rounded-md px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {t(item.labelKey)}
                </Link>
              ))}
            </nav>

            <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-3">
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="block rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              >
                {t('nav.admin')}
              </Link>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700 p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                {t('common.language')}
              </div>
              <LanguageSwitcher />
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700 p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                {t('theme.appearance')}
              </div>
              <ThemeSwitcher />
            </div>
          </aside>
        </div>,
        document.body,
      )}
    </>
  );
}
